import {ArrowDownUp, TrendingDown, TrendingUp, Zap} from 'lucide-react'
import { useContext, useMemo, useState } from 'react'
import { WalletAddressContext } from '@/lib/walletContext'
import { costForTokens, priceAtSupply, tokensForCost } from '@/lib/bondingCurve'
import { addColonyActivity, setTokenBalance } from '@/lib/colonyActivity'
import { formatCurrency, formatNumber } from '@/lib/format'

type Side = 'buy' | 'sell'

interface QuickLaunchTradeProps {
  projectId?: string
  projectName?: string
  ticker: string
  basePrice: number
  increment: number
  supply: number
  holderBalance?: number
  onSupplyChange?: (supply: number) => void
}

/**
 * Pre-bond trading widget (pump.fun-style): lets users buy or sell a
 * "quick launch" token directly against its live bonding curve before it
 * graduates ("bonds") to full open trading.
 */
const buyPresets = ['0.1', '0.5', '1', '5']

function QuickLaunchTrade({ projectId, projectName, ticker, basePrice, increment, supply, holderBalance = 0, onSupplyChange }: QuickLaunchTradeProps) {
  const address = useContext(WalletAddressContext)
  const [side, setSide] = useState<Side>('buy')
  const [amount, setAmount] = useState('0.5')
  const [confirmed, setConfirmed] = useState<null | { side: Side, summary: string }>(null)
  const [tradeError, setTradeError] = useState<string | null>(null)
  const [simSupply, setSimSupply] = useState(supply)
  const [balance, setBalance] = useState(holderBalance)

  const currentPrice = useMemo(() => priceAtSupply(basePrice, increment, simSupply), [basePrice, increment, simSupply])
  const parsedAmount = Number.parseFloat(amount) || 0

  const preview = useMemo(() => {
    if (side === 'buy') {
      const tokens = tokensForCost(basePrice, increment, simSupply, parsedAmount)
      const newPrice = priceAtSupply(basePrice, increment, simSupply + tokens)
      return { tokens, newPrice, usd: parsedAmount }
    }
    const sellSupplyStart = Math.max(0, simSupply - parsedAmount)
    const usd = costForTokens(basePrice, increment, sellSupplyStart, Math.min(parsedAmount, simSupply))
    const newPrice = priceAtSupply(basePrice, increment, sellSupplyStart)
    return { tokens: parsedAmount, newPrice, usd }
  }, [side, basePrice, increment, simSupply, parsedAmount])

  function handleTrade(e: React.FormEvent) {
    e.preventDefault()
    if (parsedAmount <= 0) {
      setTradeError('Enter an amount greater than 0.')
      return
    }
    if (side === 'sell' && parsedAmount > balance) {
      setTradeError(`You only hold ${formatNumber(balance)} ${ticker}. Reduce the amount to sell.`)
      return
    }
    setTradeError(null)
    const tokensTraded = side === 'buy' ? Math.floor(preview.tokens) : Math.floor(parsedAmount)
    const summary = side === 'buy'
      ? `Bought ~${formatNumber(tokensTraded)} ${ticker} for ${formatCurrency(preview.usd)}`
      : `Sold ${formatNumber(tokensTraded)} ${ticker} for ~${formatCurrency(preview.usd)}`
    const nextSupply = side === 'buy' ? simSupply + tokensTraded : Math.max(0, simSupply - tokensTraded)
    const nextBalance = side === 'buy' ? balance + tokensTraded : Math.max(0, balance - tokensTraded)
    setSimSupply(nextSupply)
    setBalance(nextBalance)
    onSupplyChange?.(nextSupply)
    if (projectId) {
      setTokenBalance(projectId, nextBalance, address)
      addColonyActivity({
        kind: side,
        projectId,
        projectName: projectName ?? ticker,
        ticker,
        summary,
        amountUsd: preview.usd,
        tokens: tokensTraded,
      }, address)
    }
    setConfirmed({ side, summary })
  }

  return (
    <div className="rounded-3xl border border-lime-400/20 bg-white/[0.03] p-5 backdrop-blur-md sm:p-6">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-2xl border border-lime-400/20 bg-lime-400/10 text-lime-400">
          <Zap className="h-4.5 w-4.5" aria-hidden="true" />
        </div>
        <div>
          <h3 className="font-semibold text-white">Quick launch trading</h3>
          <p className="text-xs text-zinc-500">Buy or sell directly on the curve before it bonds</p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between rounded-2xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm">
        <span className="text-zinc-400">Live price</span>
        <span className="font-semibold text-white">
          {formatCurrency(currentPrice)}
          {' '}
          per
          {' '}
          {ticker}
        </span>
      </div>

      <div className="mt-4 inline-flex w-full rounded-full border border-white/10 bg-black/50 p-1" role="tablist" aria-label="Trade side">
        <button
          type="button"
          role="tab"
          aria-selected={side === 'buy'}
          onClick={() => { setSide('buy'); setTradeError(null); setConfirmed(null) }}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
            side === 'buy' ? 'bg-lime-400 text-black' : 'text-zinc-400 hover:text-white'
          }`}
        >
          <TrendingUp className="h-4 w-4" aria-hidden="true" />
          Buy
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={side === 'sell'}
          onClick={() => { setSide('sell'); setTradeError(null); setConfirmed(null) }}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
            side === 'sell' ? 'bg-orange-400 text-black' : 'text-zinc-400 hover:text-white'
          }`}
        >
          <TrendingDown className="h-4 w-4" aria-hidden="true" />
          Sell
        </button>
      </div>

      <form onSubmit={handleTrade} className="mt-4 space-y-3">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label htmlFor="quick-trade-amount" className="block text-xs font-medium text-zinc-400">
              {side === 'buy' ? 'Amount to spend (USD)' : `Amount to sell (${ticker})`}
            </label>
            <span className="text-[11px] text-zinc-500">
              {side === 'buy' ? 'You hold' : 'Balance'}
              {' '}
              {formatNumber(balance)}
              {' '}
              {ticker}
            </span>
          </div>
          <div className="relative">
            <input
              id="quick-trade-amount"
              type="number"
              min="0"
              step="any"
              required
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setTradeError(null) }}
              aria-invalid={!!tradeError}
              aria-describedby={tradeError ? 'quick-trade-error' : undefined}
              className={`w-full rounded-xl border bg-black/50 px-3 py-2.5 pr-16 text-sm text-white focus:outline-none focus:ring-1 ${
                side === 'buy'
                  ? 'border-lime-400/30 focus:border-lime-400/50 focus:ring-lime-400/30'
                  : 'border-orange-400/30 focus:border-orange-400/50 focus:ring-orange-400/30'
              }`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500">
              {side === 'buy' ? 'USD' : ticker}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {side === 'buy'
              ? buyPresets.map(preset => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => { setAmount(preset); setTradeError(null); setConfirmed(null) }}
                    className="rounded-full border border-white/10 px-2 py-1 text-[11px] font-medium text-zinc-400 hover:border-lime-400/40 hover:text-lime-400"
                  >
                    ${preset}
                  </button>
                ))
              : (
                  <button
                    type="button"
                    onClick={() => { setAmount(String(Math.floor(balance))); setTradeError(null); setConfirmed(null) }}
                    className="rounded-full border border-white/10 px-2 py-1 text-[11px] font-medium text-zinc-400 hover:border-orange-400/40 hover:text-orange-400"
                  >
                    MAX
                  </button>
                )}
          </div>
        </div>

        <div className="flex items-center justify-center text-zinc-600">
          <ArrowDownUp className="h-3.5 w-3.5" aria-hidden="true" />
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-500">{side === 'buy' ? `You receive (~est.)` : 'You receive (~est.)'}</span>
            <span className="font-semibold text-white">
              {side === 'buy'
                ? `${formatNumber(Math.floor(preview.tokens))} ${ticker}`
                : formatCurrency(preview.usd)}
            </span>
          </div>
          <div className="mt-1.5 flex items-center justify-between text-xs">
            <span className="text-zinc-500">Price after trade</span>
            <span className={side === 'buy' ? 'text-lime-400' : 'text-orange-400'}>{formatCurrency(preview.newPrice)}</span>
          </div>
        </div>

        {tradeError && (
          <p id="quick-trade-error" className="text-center text-xs text-red-400" role="alert">
            {tradeError}
          </p>
        )}

        <button
          type="submit"
          className={`flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:opacity-90 ${
            side === 'buy' ? 'bg-lime-400' : 'bg-orange-400'
          }`}
        >
          {side === 'buy' ? <TrendingUp className="h-4 w-4" aria-hidden="true" /> : <TrendingDown className="h-4 w-4" aria-hidden="true" />}
          {side === 'buy' ? `Buy ${ticker}` : `Sell ${ticker}`}
        </button>

        {confirmed && (
          <p className={`text-center text-xs ${confirmed.side === 'buy' ? 'text-lime-400' : 'text-orange-400'}`} role="status">
            Simulated:
            {' '}
            {confirmed.summary}
            {' '}
            — no real funds moved.
          </p>
        )}
      </form>

      <p className="mt-4 text-center text-[11px] text-zinc-500">
        100% of supply trades on the curve until it bonds, then liquidity graduates to the open market.
      </p>
    </div>
  )
}

export default QuickLaunchTrade
